import * as yup from 'yup';
import { FIELD_VALIDATION_RULES } from '../components/pageComponents/EditProfile/config/formConfig';

const passwordCheck = /^[\s\S]{8,32}$/;
const seasonCheck = /[0-9][1-9]\/[0-9][1-9]/i;

export const signupSchema = yup.object().shape({
    firstName: yup.string().min(2).required('Firstname is required'),
    lastName: yup.string().min(2).required('Lastname is required'),
    userType: yup.string().required('User type is required'),
    email: yup
        .string()
        .email('Please enter a valid email')
        .required('Email is required'),
    password: yup
        .string()
        .min(8)
        .matches(passwordCheck, {
            message: 'Must contain at least one number and alphabet'
        })
        .required('Password field is empty'),
    terms: yup
        .boolean()
        .oneOf([true], 'You must accept the terms and conditions')
});

export const forgotPassword2Schema = yup.object().shape({
    verificationCode: yup
        .number()
        .integer('Must be a number')
        .min(8)
        .required('Code cannot be empty'),
    password: yup.string().min(8).matches(passwordCheck, {
        message: 'Must contain at least one number and alphabet'
    })
});

export const forgotPasswordSchema = yup.object().shape({
    email: yup
        .string()
        .email('Please enter a valid email')
        .required('Email is required')
});

export const loginSchema = yup.object().shape({
    email: yup
        .string()
        .email('Please enter a valid email')
        .required('Email is required'),
    password: yup
        .string()
        .min(8)
        .matches(passwordCheck, {
            message: 'Must contain at least one number and alphabet'
        })
        .required('Password field is empty')
});

export const createProfile1 = yup.object().shape({
    userType: yup.string().required('User type is required'), // Add required
    position: yup.string().when('userType', {
        is: 'PLAYER',
        then: () => yup.string().required('Position is required for players')
    }),
    locationId: yup.string().required('Location is required'),
    nonPlayerRole: yup.array().when('userType', {
        is: 'NON_PLAYER',
        then: () =>
            yup
                .array()
                .min(1, 'Select at least one role')
                .of(yup.string())
                .required('Role is required')
    }),
    gender: yup.string().when('userType', {
        is: 'PLAYER',
        then: () => yup.string().required('Please pick a gender')
    }),
    preferredFoot: yup.string().when('userType', {
        is: 'PLAYER',
        then: () =>
            yup.string().required('Preferred foot is required for players')
    }),
    birthday: yup.date().when('userType', {
        is: 'PLAYER',
        then: () => {
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - 16);
            return yup
                .date()
                .typeError('Please enter a valid date')
                .max(
                    cutoff,
                    'You must be at least 16 years old to create an account.'
                )
                .required('Birthday is required for players');
        }
    }),
    height: yup.string().when('userType', {
        is: 'PLAYER',
        then: () =>
            yup
                .string()
                .matches(
                    FIELD_VALIDATION_RULES.height.pattern,
                    'Height must be in format 0.00'
                )
                .required('Please enter your height')
    })
});

export const editStats = yup.object().shape({
    teamName: yup.string(),
    seasonName: yup.string().matches(seasonCheck, {
        message: 'Must follow the pattern YY/YY'
    }),
    goals: yup.string(),
    teamLogo: yup.string(),
    assists: yup.string(),
    appearances: yup.string(),
    cleanSheets: yup.string()
});

export const radioButtons = yup.object().shape({
    clubAdmin: yup.boolean(),
    haveAuthority: yup.boolean()
});

export const editProfile = yup.object().shape({
    // Profile fields
    firstName: yup
        .string()
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name must not exceed 50 characters')
        .required('First name is required'),
    lastName: yup
        .string()
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name must not exceed 50 characters')
        .required('Last name is required'),
    bio: yup
        .string()
        .min(10, 'Bio must be at least 10 characters')
        .max(500, 'Bio must not exceed 500 characters')
        .optional(),
    locationId: yup.string().required('Location is required'),
    height: yup
        .string()
        .matches(/^\d*\.?\d+$/, 'Height must be a valid number')
        .optional(),
    birthday: yup
        .date()
        .max(new Date(), 'Birthday cannot be in the future')
        .optional(),

    // Player-specific fields
    position: yup.string().optional(),
    preferredFoot: yup
        .string()
        .oneOf(
            ['LEFT', 'RIGHT', 'BOTH'],
            'Please select a valid preferred foot'
        )
        .optional(),
    secondaryPositions: yup
        .array()
        .of(yup.string())
        .max(3, 'You can select maximum 3 secondary positions')
        .optional(),

    // Club fields
    clubId: yup
        .string()
        .min(2, 'Club name must be at least 2 characters')
        .max(100, 'Club name must not exceed 100 characters')
        .optional(),
    teamId: yup
        .string()
        .min(2, 'Team name must be at least 2 characters')
        .max(100, 'Team name must not exceed 100 characters')
        .optional(),
    contractStatus: yup
        .string()
        .oneOf(
            ['unsigned', 'contract', 'nonContract'],
            'Please select a valid contract status'
        )
        .optional(),
    contractExpiry: yup.string().optional(),

    // Availability fields
    eligibleNations: yup
        .array()
        .of(yup.string())
        .max(3, 'You can select maximum 3 countries for national eligibility')
        .optional(),
    representedBy: yup
        .string()
        .max(100, 'Represented by must not exceed 100 characters')
        .optional(),
    openToTrials: yup.boolean().optional(),

    // Non-player fields
    nonPlayerRole: yup
        .array()
        .of(yup.string())
        .max(3, 'You can select maximum 3 roles')
        .optional(),

    // System fields (usually not validated but included for completeness)
    clubId: yup.string().optional(),
    teamId: yup.string().optional()
});

export const experienceScheme = yup.object().shape({
    teamName: yup.string(),

    name: yup.string().matches(seasonCheck, {
        message: 'Must follow the pattern YY/YY'
    }),
    goals: yup.string(),
    assists: yup.string(),
    apperance: yup.string(),
    cleanSheets: yup.string()
});

export const changePasswordScheme = yup.object().shape({
    oldPassword: yup.string().min(8).required('Password field is empty'),

    newPassword: yup
        .string()
        .min(8)
        .matches(passwordCheck, {
            message: 'Must contain at least one number and alphabet'
        })
        .required('Password field is empty'),

    confirmPassword: yup
        .string()
        .oneOf([yup.ref('newPassword'), null], 'New Passwords do not match')
        .required('Confirm password')
});

export const referenceFormSchema = yup.object().shape({
    relationship: yup.string().min(3).required('Relationship is required'),
    positionAtTime: yup
        .array()
        .min(1, 'Select at least one position')
        .of(yup.string())
        .required('Your position at the time is required'),

    message: yup.string().required('Message for the reference'),
    toUserId: yup.string().required('You need to select a user')
});
